/**
 * Keeps open browsers off a build we already replaced.
 *
 * The bundle knows which build it is (src/buildVersion.js, stamped at build
 * time). The backend knows which build it expects (GET /app-version/, set by
 * the deploy). Different answers mean this tab is running stale code, and the
 * person in front of it has no way of knowing — the page looks fine, it just
 * calls APIs that have moved on.
 *
 * Two rules govern what we do about it, and the split matters:
 *
 *   * Stale at page load        → reload immediately. Nothing is in flight and
 *                                 nobody has typed anything yet.
 *   * Goes stale while open     → show a banner, never reload underneath them.
 *                                 Auto-reloading a half-filled lead form is a
 *                                 worse bug than the stale bundle was.
 *
 * Everything here fails open. No answer, an unparseable answer, an empty
 * version, a backend that doesn't have the endpoint yet — all mean "carry on".
 * The only thing worse than a stale tab is every tab reload-looping at once,
 * so a reload needs positive evidence of a mismatch, never the absence of it.
 */

import { API_URL, APP_VERSION_URL } from '../constants/api';
import { BUILD_VERSION } from '../buildVersion';

// How often an open tab re-checks. Long enough to be free, short enough that
// somebody who leaves the CRM open all day still sees the banner the same day.
export const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;

// Don't re-check more than this often, however many focus/visibility events
// the browser decides to fire.
const MIN_CHECK_GAP_MS = 30 * 1000;

const RELOAD_GUARD_KEY = 'app_version_reload_guard';

// A tab may try this many times to reach a given version before we stop
// reloading and put a manual button in front of the user instead. Without this,
// an index.html pinned by a proxy that ignores no-cache would loop forever.
const MAX_RELOAD_ATTEMPTS = 2;

let lastCheckAt = 0;

const versionEndpoint = () => `${String(API_URL).replace(/\/+$/, '')}/${APP_VERSION_URL}`;

/** The build this tab is running. */
export const currentBuildVersion = () => String(BUILD_VERSION || '').trim();

function readGuard() {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (err) {
    // Private mode, or storage disabled. No guard means we still reload once —
    // we just can't count attempts, so MAX_RELOAD_ATTEMPTS can't be enforced.
    return null;
  }
}

function writeGuard(value) {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, JSON.stringify(value));
  } catch (err) {
    /* nothing we can do, and nothing that should stop the reload */
  }
}

function clearGuard() {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch (err) {
    /* ignore */
  }
}

/**
 * Ask the backend which build it expects.
 * Returns the version string, or '' for "no opinion / could not tell".
 */
export async function fetchServerVersion({ signal } = {}) {
  try {
    const res = await fetch(versionEndpoint(), {
      method: 'GET',
      // Both needed: `cache` stops the browser reusing its own copy, the header
      // stops any proxy in between handing back a stale one. A cached version
      // check can never report a new version, which defeats the whole feature.
      cache: 'no-store',
      headers: { 'Cache-Control': 'no-cache' },
      credentials: 'omit',
      signal,
    });
    if (!res.ok) return '';
    const data = await res.json();
    return String(data?.version || '').trim();
  } catch (err) {
    // Offline, CORS, backend not yet deployed with this endpoint — all of them
    // mean we simply don't know, which is not grounds for reloading anyone.
    return '';
  }
}

/** True when the server has published a build and it isn't the one we're running. */
export function isStale(serverVersion) {
  const mine = currentBuildVersion();
  if (!serverVersion || !mine) return false;
  return serverVersion !== mine;
}

/** Throw away anything that could serve us the old bundle again. */
async function dropCaches() {
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch (err) {
    /* best effort */
  }
  try {
    if (navigator.serviceWorker?.getRegistrations) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((reg) => reg.unregister()));
    }
  } catch (err) {
    /* best effort */
  }
}

/**
 * Reload onto the published build, escalating if a previous attempt didn't take.
 *
 * Attempt 1 clears caches and reloads — enough whenever index.html is served
 * with no-cache, which is the deployment this expects. Attempt 2 adds a
 * cache-busting query string, which forces a fresh index.html even through a
 * proxy that ignored the headers. After that we stop and hand it to the user,
 * because a third failure means something we can't fix from here.
 *
 * Returns false when we've given up, so the caller can show the banner instead.
 */
export async function reloadForVersion(serverVersion) {
  const guard = readGuard();
  const attempts = guard && guard.version === serverVersion ? Number(guard.attempts) || 0 : 0;

  if (attempts >= MAX_RELOAD_ATTEMPTS) {
    return false;
  }

  writeGuard({ version: serverVersion, attempts: attempts + 1, at: Date.now() });
  await dropCaches();

  if (attempts === 0) {
    window.location.reload();
    return true;
  }

  const url = new URL(window.location.href);
  url.searchParams.set('_v', serverVersion);
  window.location.replace(url.toString());
  return true;
}

/** Called once the running build matches — the next mismatch starts fresh. */
export function noteVersionMatched() {
  const guard = readGuard();
  if (guard) clearGuard();

  // Tidy away the cache-buster a second reload attempt may have added, so it
  // can't be bookmarked or pasted into chat and outlive the build it busted.
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has('_v')) {
      url.searchParams.delete('_v');
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }
  } catch (err) {
    /* cosmetic only */
  }
}

/**
 * One check. `auto` decides what a mismatch means:
 *   true  → reload now (page load: nothing to lose)
 *   false → report it and let the caller show a banner (tab in use)
 *
 * Returns the stale server version, or '' when we're up to date or unsure.
 */
export async function checkVersion({ auto = false, force = false, signal } = {}) {
  const now = Date.now();
  if (!force && now - lastCheckAt < MIN_CHECK_GAP_MS) return '';
  lastCheckAt = now;

  const serverVersion = await fetchServerVersion({ signal });
  if (!isStale(serverVersion)) {
    if (serverVersion) noteVersionMatched();
    return '';
  }

  if (auto) {
    const reloading = await reloadForVersion(serverVersion);
    // Gave up reloading — hand it back so the user gets a button instead.
    return reloading ? '' : serverVersion;
  }
  return serverVersion;
}
